import './main.css';
import './themes.css';
import { Constants } from './internal/Constants';
import { App } from './root/App';
import { Logger } from '@shared/common';
import { RecordLoggerFrontend, RegisterViewportChange, SetRootFontSize, Telemetry, TelemetryClient } from '@shared/frontend';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

/** File-local constants for lobby bootstrap. */
const localConstants = { telemetryCleanupEvent: 'pagehide' } as const;

/**
 * Applies the shared adaptive root font-size with the lobby's portrait boost so every rem-based dimension (typography, padding, margins, gaps) scales up uniformly when the device is held
 * upright.
 */
const applyLobbyRootFontSize = (): void => {
  SetRootFontSize(Constants.PortraitSpacingScale);
};

/**
 * Initializes the React application and mounts it to the DOM.
 *
 * - Renders the App component inside React.StrictMode (dev-only helper).
 * - Detects impure render logic via intentional extra dev renders.
 * - Surfaces missing useEffect cleanups by re-running Effects on mount.
 * - Warns about legacy/deprecated APIs (e.g., string refs, findDOMNode).
 * - Helps reveal unexpected side effects via double-invoking certain hooks.
 */

/** Safely initialize React root element with null check. */
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

// Install the durable log recorder so all subsequent Logger.Error/Warn calls also flow into MongoDB via the lobby `/log` endpoint.
Logger.SetRecorder(new RecordLoggerFrontend());

void TelemetryClient.Init();
/** Cleanup function for passive telemetry listeners. */
const cleanupTelemetry = Telemetry.RegisterPassiveSignals();
window.addEventListener(localConstants.telemetryCleanupEvent, cleanupTelemetry, { once: true });

// Count every visit (anonymous or returning) the moment the app boots, before any auth resolves, so landing-page traffic is measured.
Telemetry.SessionStart(Telemetry.Source.Anonymous);

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>
);

// Set initial root font size and re-run on every viewport change (resize, rotation, mobile chrome show/hide).
window.addEventListener('DOMContentLoaded', applyLobbyRootFontSize);
RegisterViewportChange(applyLobbyRootFontSize);
